/**
 * Timezone adapter utilities using date-fns-tz
 *
 * Provides timezone-aware date operations, particularly for Europe/Berlin timezone
 * used in restaurant voting logic.
 */

import {format, parseISO} from 'date-fns'
import {
  formatInTimeZone as fnsFormatInTimeZone,
  toZonedTime as fnsToZonedTime,
  fromZonedTime as fnsFromZonedTime,
  getTimezoneOffset,
} from 'date-fns-tz'

/**
 * Get the local timezone identifier
 * Replaces: momentTZ.tz.guess()
 *
 * @returns Timezone identifier (e.g., 'America/New_York', 'Europe/Berlin')
 */
export const getLocalTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Format a date in a specific timezone
 * Replaces: moment.tz(date, timezone).format(formatString)
 *
 * @param date - Date to format
 * @param timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @param formatString - date-fns format string
 * @returns Formatted date string in the specified timezone
 */
export const formatInTimeZone = (
  date: Date | string | number,
  timezone: string,
  formatString: string
): string => {
  const dateObj =
    typeof date === 'string'
      ? parseISO(date)
      : typeof date === 'number'
        ? new Date(date)
        : date
  return fnsFormatInTimeZone(dateObj, timezone, formatString)
}

/**
 * Convert a date from a specific timezone to UTC
 * Replaces: moment.tz(dateString, formatString, timezone).utc()
 *
 * @param date - Date in the specified timezone
 * @param timezone - IANA timezone identifier
 * @returns Date object in UTC
 */
export const convertZonedTimeToUtc = (date: Date, timezone: string): Date => {
  return fnsFromZonedTime(date, timezone)
}

/**
 * Convert a UTC date to a specific timezone
 * Replaces: moment.utc(date).tz(timezone)
 *
 * @param date - UTC date
 * @param timezone - IANA timezone identifier
 * @returns Date object adjusted to the timezone
 */
export const convertUtcToZonedTime = (date: Date, timezone: string): Date => {
  return fnsToZonedTime(date, timezone)
}

/**
 * Get timezone offset in milliseconds
 * Replaces: moment.tz.zone(timezone).utcOffset(date) * 60000
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to get offset for (default: now)
 * @returns Offset in milliseconds
 */
export const getTimezoneOffsetMs = (
  timezone: string,
  date: Date = new Date()
): number => {
  return getTimezoneOffset(timezone, date)
}

/**
 * Convert UTC date string to local timezone and format
 * Replaces: moment.utc(dateString).tz(localTimezone).format(formatString)
 *
 * @param utcDateString - UTC date string (ISO format)
 * @param formatString - date-fns format string
 * @param timezone - Target timezone (default: local)
 * @returns Formatted date string in target timezone
 */
export const formatUtcToLocal = (
  utcDateString: string,
  formatString: string,
  timezone: string = getLocalTimezone()
): string => {
  const utcDate = parseISO(utcDateString)
  const zonedDate = fnsToZonedTime(utcDate, timezone)
  return format(zonedDate, formatString)
}

/**
 * Parse a date string as if it's in a specific timezone
 * Replaces: moment.tz(dateString, formatString, timezone)
 *
 * @param dateString - Date string to parse
 * @param timezone - Timezone to interpret the date in
 * @returns UTC Date object
 */
export const parseInTimeZone = (dateString: string, timezone: string): Date => {
  const parsed = parseISO(dateString)
  return fnsFromZonedTime(parsed, timezone)
}

/**
 * Check if a timezone observes Daylight Saving Time at a given date
 *
 * @param timezone - IANA timezone identifier
 * @param date - Date to check (default: now)
 * @returns true if DST is in effect
 */
export const isDaylightSavingTime = (
  timezone: string,
  date: Date = new Date()
): boolean => {
  const january = new Date(date.getFullYear(), 0, 1)
  const july = new Date(date.getFullYear(), 6, 1)
  const janOffset = getTimezoneOffset(timezone, january)
  const julyOffset = getTimezoneOffset(timezone, july)
  const currentOffset = getTimezoneOffset(timezone, date)

  return currentOffset !== Math.max(janOffset, julyOffset)
}
